// ── Record focus ──────────────────────────────────────────────────────────────
// One mechanism behind "click the alert → land on the record". An alert row (an
// attention panel, a notification, a dashboard attention item) names a record; a
// page renders that record somewhere in a list. This module joins the two:
//
//   • the alert side calls `focusPath('/sales', { id, label })` and navigates, or
//     `requestFocus([...])` when the record is already on the current page;
//   • the page side spreads `focusAttrs(row.id, row.code)` on each row and calls
//     `useRecordFocus()` once, which reads the params off the URL and hands the
//     request over;
//   • `usePaged` (shared pager) jumps to the page holding the record, so a hit on
//     page 4 of a table is not silently invisible.
//
// The request is kept in a tiny module-level store rather than React state so the
// three producers (URL, in-page click, pager) all see the same one, and the DOM
// hunt survives the re-renders that happen while the target list is still loading.

export const FOCUS_PARAM = 'focus';
export const FOCUS_LABEL_PARAM = 'focusLabel';
export const FOCUS_SECTION_PARAM = 'focusSection';

const FLASH_CLASS = 'record-focus-flash';
const SECTION_FLASH_CLASS = 'record-focus-section-flash';
// 3 pulses of 1.4s (see `recordFocusFlash` in index.css) + a little slack.
const FLASH_MS = 4400;
// 2 pulses of 1.6s for the section fallback — a whole panel blinking as hard as a
// row would read as an error rather than as "it is in here".
const SECTION_FLASH_MS = 3500;
// Lists load after the navigation, so the element usually is not there on the
// first look. Keep hunting for a while, then give up quietly.
const HUNT_MS = 10000;
const HUNT_INTERVAL_MS = 120;
// How long to hold out for the exact row before settling for its section. Long
// enough for a fetch to land, short enough that the operator is not left staring
// at an unchanged page wondering whether the click registered.
const SECTION_FALLBACK_MS = 2600;

let request = null;      // { keys: string[], section: string|null, token: number }
let huntTimer = null;
let huntStartedAt = 0;
// The page the hunt was started for. A hunt runs for up to 10s, so the operator can
// easily be somewhere else by the time it gives up — and "X is not available on this
// screen" about a record they asked for on a *different* screen reads as a fault in
// the page they are now looking at. Leaving the page abandons the hunt silently.
let huntPath = null;
const listeners = new Set();
// Told when a hunt ends with nothing found — no row, no section. Silence there is
// the worst answer: the operator clicked an alert, the screen did not move, and
// they cannot tell whether the click registered or the record is gone. One
// subscriber (the app shell) turns this into a message.
let missHandler = null;

/** Register the "record could not be shown" reporter. Returns an unsubscribe. */
export function setFocusMissHandler(fn) {
  missHandler = fn;
  return () => { if (missHandler === fn) missHandler = null; };
}

function reportMiss(keys) {
  if (!missHandler) return;
  try {
    missHandler(focusKeys(keys));
  } catch {
    /* a bad reporter must not break the hunt */
  }
}

/** Non-empty, de-duplicated, string-ified keys — callers pass whatever they have
 *  (`id`, `lot_number`, `fg_code`, …) and blanks are normal. */
export function focusKeys(...values) {
  const out = [];
  for (const value of values.flat()) {
    if (value === null || value === undefined) continue;
    const key = String(value).trim();
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}

export function getFocusRequest() {
  return request;
}

export function subscribeFocus(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(request);
    } catch {
      /* a bad subscriber must not stop the others */
    }
  });
}

function stopHunt() {
  if (huntTimer) {
    window.clearInterval(huntTimer);
    huntTimer = null;
  }
}

function selectorFor(keys) {
  return keys
    .map((k) => {
      const esc = window.CSS?.escape ? window.CSS.escape(k) : k.replace(/"/g, '\\"');
      return `[data-focus-id="${esc}"],[data-focus-label="${esc}"]`;
    })
    .join(',');
}

/** Scroll the record into the middle of the viewport and pulse it. Re-running on
 *  an element already flashing restarts the animation rather than stacking. */
export function flashElement(el) {
  if (!el) return;
  el.classList.remove(FLASH_CLASS);
  // Force a reflow so removing and re-adding the class replays the animation.
  void el.offsetWidth;
  el.classList.add(FLASH_CLASS);
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch {
    el.scrollIntoView();
  }
  window.setTimeout(() => el.classList.remove(FLASH_CLASS), FLASH_MS);
}

/** Scroll a section into view and outline it — the fallback for a record that is
 *  not on screen as its own row (a production order, a tank lot, an item the page
 *  simply does not list). Landing on the right panel with it marked still tells the
 *  operator where to look; silently doing nothing does not. */
export function flashSection(el) {
  if (!el) return;
  el.classList.remove(SECTION_FLASH_CLASS);
  void el.offsetWidth;
  el.classList.add(SECTION_FLASH_CLASS);
  try {
    // `start`, not `center`: a section is usually taller than the viewport, and its
    // heading is the part worth reading.
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch {
    el.scrollIntoView();
  }
  window.setTimeout(() => el.classList.remove(SECTION_FLASH_CLASS), SECTION_FLASH_MS);
}

function sectionElement(section) {
  if (!section) return null;
  const esc = window.CSS?.escape ? window.CSS.escape(section) : section.replace(/"/g, '\\"');
  return document.querySelector(`[data-focus-section="${esc}"]`);
}

/** Ask for a record to be revealed. Producers do not know whether the row is
 *  rendered yet, so the hunt is retried until it appears or the window closes; if
 *  it never appears, the section it belongs to is highlighted instead. */
export function requestFocus(keys, { section = null } = {}) {
  const wanted = focusKeys(keys);
  if (wanted.length === 0 && !section) return;
  request = { keys: wanted, section: section || null, token: Date.now() };
  huntStartedAt = Date.now();
  huntPath = window.location.pathname;
  emit();

  const settle = (el, flash) => {
    stopHunt();
    flash(el);
    // Held a moment longer than the flash so a pager that mounts late still sees
    // the request and does not fight the animation.
    window.setTimeout(clearFocus, FLASH_MS);
  };

  stopHunt();
  const look = () => {
    if (!request) return stopHunt();
    // Navigated away mid-hunt: the request belonged to the previous screen, so give
    // up without a word rather than blaming the page the operator is on now.
    if (huntPath && window.location.pathname !== huntPath) {
      stopHunt();
      clearFocus();
      return undefined;
    }
    const waited = Date.now() - huntStartedAt;
    const el = wanted.length ? document.querySelector(selectorFor(request.keys)) : null;
    if (el) return settle(el, flashElement);
    // No row: fall back to its section — straight away when the caller named no
    // record at all, otherwise once the grace period is up AND the list has actually
    // rendered rows. Without that second test a slow fetch would get the section
    // flash while the record was still on its way, and the row itself would then
    // never be marked.
    const sectionEl = sectionElement(request.section);
    if (sectionEl && (!wanted.length
      || (waited > SECTION_FALLBACK_MS && sectionEl.querySelector('[data-focus-id]')))) {
      return settle(sectionEl, flashSection);
    }
    // Deadline: mark the section if there is one, so a list that never renders a
    // single row (empty, still failing to load) is at least pointed at. With no
    // section either, the record simply is not on this screen — say so.
    if (waited > HUNT_MS) {
      if (sectionEl) return settle(sectionEl, flashSection);
      stopHunt();
      reportMiss(wanted);
      clearFocus();
    }
    return undefined;
  };
  look();
  if (!huntTimer && request) huntTimer = window.setInterval(look, HUNT_INTERVAL_MS);
}

export function clearFocus() {
  stopHunt();
  if (!request) return;
  request = null;
  emit();
}

// Fallback section per destination page, for links that name a record but no
// section — notifications and the dashboard feed carry a path and an id, nothing
// more. Only pages that actually mark a panel appear here; anywhere else simply has
// no section to fall back to, which is fine.
const SECTION_BY_PATH = {
  '/sales': 'sales-orders',
  '/finished-goods': 'finished-goods',
  '/cold-storage/intake': 'cold-storage-inventory',
  '/procurement-batches': 'preprocessing-lots',
  '/purchase-risk-alerts': 'risk-alerts',
  '/purchase-invoices': 'purchase-invoices',
  '/agents': 'agents',
};

/** The section to highlight when the exact record cannot be found on `path`. An
 *  explicit section always wins — the caller knows which panel it meant. */
export function resolveSection(path, section) {
  if (section) return section;
  return SECTION_BY_PATH[String(path || '').split('?')[0]] || null;
}

/** Spread onto the panel that holds a kind of record ("the sales order table"), so
 *  a record the page does not list as its own row can still be answered with "it is
 *  in here". Names are the slugs in `RECORD_ROUTES` in AttentionPanel.js. */
export function sectionAttrs(section) {
  return section ? { 'data-focus-section': String(section) } : {};
}

/** Spread onto the element that represents one record — a table row, a card.
 *  Both the id and the human code are matched, so a link built from either works. */
export function focusAttrs(id, label) {
  const attrs = {};
  if (id !== null && id !== undefined && String(id) !== '') attrs['data-focus-id'] = String(id);
  if (label !== null && label !== undefined && String(label) !== '') {
    attrs['data-focus-label'] = String(label);
  }
  return attrs;
}

/** Build the deep link an alert should point at: `/sales?tab=orders&focus=<id>`.
 *  `label` may be a list — a page can render the uuid, the printed code, or the
 *  batch/slot code from the locator, and any of them is a valid way to find the
 *  row, so every candidate is carried as a repeated `focusLabel`. */
export function focusPath(path, { id, label, section, params } = {}) {
  const [base, existing] = String(path || '').split('?');
  const search = new URLSearchParams(existing || '');
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') search.set(k, String(v));
  });
  if (id) search.set(FOCUS_PARAM, String(id));
  focusKeys(label).forEach((k) => search.append(FOCUS_LABEL_PARAM, k));
  const resolved = resolveSection(base, section);
  if (resolved) search.set(FOCUS_SECTION_PARAM, resolved);
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

/** True when the link already targets the page the user is looking at, in which
 *  case the alert flashes the row in place instead of re-navigating. */
export function isSamePath(path, pathname) {
  const base = String(path || '').split('?')[0];
  return base === pathname;
}
